import { ContentRun } from './store/types';

// FUSION DENTAL's own reference templates use <<Field Name>> as inline mail-merge tokens,
// so that syntax doubles as the editable raw text for a content array in the Properties panel.
const VARIABLE_TOKEN = /<<([^<>]+)>>/g;

export function runsToText(runs: ContentRun[] | undefined): string {
  if (!runs || runs.length === 0) return '';
  return runs.map((r) => (r.type === 'variable' ? `<<${r.fieldKey}>>` : r.value)).join('');
}

export function textToRuns(text: string): ContentRun[] {
  const runs: ContentRun[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  VARIABLE_TOKEN.lastIndex = 0;
  while ((match = VARIABLE_TOKEN.exec(text)) !== null) {
    if (match.index > lastIndex) {
      runs.push({ type: 'text', value: text.slice(lastIndex, match.index) });
    }
    runs.push({ type: 'variable', fieldKey: match[1] });
    lastIndex = match.index + match[0].length;
  }
  if (lastIndex < text.length) {
    runs.push({ type: 'text', value: text.slice(lastIndex) });
  }
  return runs;
}

export function appendRuns(runs: ContentRun[] | undefined, toAppend: ContentRun[]): ContentRun[] {
  const existing = runs && runs.length ? [...runs] : [];
  if (existing.length > 0 && toAppend.length > 0) {
    const last = existing[existing.length - 1];
    if (last.type === 'text' && !last.value.endsWith(' ')) {
      existing[existing.length - 1] = { ...last, value: `${last.value} ` };
    }
  }
  existing.push(...toAppend);
  return existing;
}

export function appendVariableRun(runs: ContentRun[] | undefined, fieldKey: string): ContentRun[] {
  return appendRuns(runs, [{ type: 'variable', fieldKey }]);
}

const UNDERSCORE_RUN = /_{3,}/g;

// Reused across calls -- creating a canvas per keystroke/drop would be wasteful,
// and this is the only place in the designer that needs real text-width
// measurement (everywhere else uses a flat px-per-char guess, which is fine for
// coarse layout spacing but not precise enough to tell which run/character a
// drop point landed on).
let measureCanvasCtx: CanvasRenderingContext2D | null | undefined;

function measureTextWidth(text: string, fontSize: number, fontFamily: string, fontWeight?: string): number {
  if (measureCanvasCtx === undefined) {
    measureCanvasCtx = typeof document !== 'undefined' ? document.createElement('canvas').getContext('2d') : null;
  }
  if (!measureCanvasCtx) return text.length * fontSize * 0.55; // non-DOM fallback (e.g. tests)
  measureCanvasCtx.font = `${fontWeight === 'bold' ? 'bold ' : ''}${fontSize}px ${fontFamily || 'Arial'}`;
  return measureCanvasCtx.measureText(text).width;
}

function runText(run: ContentRun): string {
  if (run.type === 'variable') return `$${run.fieldKey}$`;
  if (run.type === 'text') return run.value;
  return run.value || '';
}

/**
 * Insert a variable run at the run/character position under `localX` (the
 * drop's x offset from the target element's own left edge, in the same
 * page-space px used for every element's x/y) instead of always appending to
 * the end -- so dropping a field wherever on a line lands it there, matching
 * where the pointer actually was, rather than jumping to whatever the last
 * run in that element happens to be.
 *
 * A drop landing on (or within a couple px of) a run of 3+ underscores --
 * the conventional "fill in the blank" placeholder these imported PDF/DOCX
 * templates already use -- replaces that whole blank rather than splitting
 * it mid-underscore, since that's unambiguously what the blank is for.
 */
export function insertVariableRunAtOffset(
  runs: ContentRun[] | undefined,
  fieldKey: string,
  localX: number,
  style: { fontSize?: number; fontFamily?: string; fontWeight?: 'normal' | 'bold'; padding?: number }
): ContentRun[] {
  const existing = runs && runs.length ? [...runs] : [];
  if (existing.length === 0) {
    return [{ type: 'variable', fieldKey }];
  }

  const fontSize = typeof style.fontSize === 'number' ? style.fontSize : 12;
  const fontFamily = style.fontFamily || 'Arial';
  const fontWeight = style.fontWeight;
  const targetX = Math.max(0, localX - (style.padding ?? 0));

  // Walk runs left-to-right accumulating rendered width until the one
  // spanning targetX is found; if targetX is past everything, the loop
  // naturally lands on the last run (matching the old append-at-end
  // behavior for a drop past the visible text).
  let cumulative = 0;
  let hitIndex = existing.length - 1;
  let hitOffsetX = 0;
  for (let i = 0; i < existing.length; i++) {
    const width = measureTextWidth(runText(existing[i]), fontSize, fontFamily, fontWeight);
    if (targetX <= cumulative + width || i === existing.length - 1) {
      hitIndex = i;
      hitOffsetX = targetX - cumulative;
      break;
    }
    cumulative += width;
  }

  const hitRun = existing[hitIndex];

  // Variable/input runs are atomic (can't split "$field$" partway through) --
  // a drop landing on one just inserts immediately before it.
  if (hitRun.type !== 'text') {
    existing.splice(hitIndex, 0, { type: 'variable', fieldKey });
    return existing;
  }

  const text = hitRun.value;
  let charIndex = text.length;
  for (let c = 0; c <= text.length; c++) {
    if (measureTextWidth(text.slice(0, c), fontSize, fontFamily, fontWeight) >= hitOffsetX) {
      charIndex = c;
      break;
    }
  }

  // A line commonly has several blanks ("Patient Name: ___ DOB: ___ SSN:
  // ___") -- find every underscore run and snap to whichever one is nearest
  // charIndex, not just the first one in the text, or a drop on the SSN
  // blank would incorrectly snap onto the Patient Name blank instead.
  let nearestBlank: { start: number; end: number } | null = null;
  let nearestBlankDistance = Infinity;
  UNDERSCORE_RUN.lastIndex = 0;
  let m: RegExpExecArray | null;
  while ((m = UNDERSCORE_RUN.exec(text)) !== null) {
    const start = m.index;
    const end = start + m[0].length;
    const distance = charIndex < start ? start - charIndex : charIndex > end ? charIndex - end : 0;
    if (distance < nearestBlankDistance) {
      nearestBlankDistance = distance;
      nearestBlank = { start, end };
    }
  }
  const splitAt = nearestBlank && nearestBlankDistance <= 2
    ? nearestBlank
    : { start: charIndex, end: charIndex };

  // Splitting mid-prose (no blank to snap to) can land right inside a word
  // or between two words with no space of its own -- pad with a space on
  // whichever side actually needs one so "Relationship to Subscriber:"
  // doesn't turn into "Relationship to $field$Subscriber:".
  const isBlankSnap = nearestBlank !== null && nearestBlankDistance <= 2;
  let before = text.slice(0, splitAt.start);
  let after = text.slice(splitAt.end);
  if (!isBlankSnap) {
    if (before && !/\s$/.test(before)) before += ' ';
    if (after && !/^\s/.test(after)) after = ' ' + after;
  }
  const newRuns: ContentRun[] = [];
  if (before) newRuns.push({ type: 'text', value: before });
  newRuns.push({ type: 'variable', fieldKey });
  if (after) newRuns.push({ type: 'text', value: after });
  existing.splice(hitIndex, 1, ...newRuns);
  return existing;
}
